import get from 'lodash/get';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import {
	REQUEST_TYPE,
	ISSUE_TYPE,
	UNASSIGNED_REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type {
	Project,
	IssueType,
	RequestType as TransformedRequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformStargateUrl } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-stargate-url/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ProjectEntity,
	IssueTypeEntity,
	TransformedProjectField,
	TransformedIssueField,
	TransformedRequestField,
} from '../../types/context-fields.tsx';
import type {
	JiraIssueTypeField,
	JiraProject,
	JiraProjectField,
	JsmRequestTypeField,
	JsmRequestType,
	JiraProjectType as GQLJiraProjectType,
} from '../../types/create-config-query.tsx';

const projectTypeCaseMapping: Record<GQLJiraProjectType, ProjectType> = {
	SOFTWARE: 'software',
	SERVICE_DESK: 'service_desk',
	BUSINESS: 'business',
	PRODUCT_DISCOVERY: 'product_discovery',
	CUSTOMER_SERVICE: 'customer_service',
};

const transformProjectEntity = ({
	id,
	name,
	avatar,
	projectType,
	simplified,
	key,
	classificationTags = [],
}: JiraProject): Project => ({
	label: `${name} (${key})`,
	value: id,
	iconUrl: avatar.large,
	// @ts-expect-error - projectType.toLowerCase is not type compliant, but will be resolved when feature gate is removed.
	projectType: fg('gic-typing-improvements')
		? projectTypeCaseMapping[projectType]
		: projectType && projectType.toLowerCase(),
	projectKey: key,
	isSimplified: simplified,
	classificationTags,
});

const transformProjects = (projects: ProjectEntity[]): Project[] =>
	projects.map((project: ProjectEntity) => transformProjectEntity(project));

const transformProjectField = (projectResponse: JiraProjectField): TransformedProjectField => {
	const { project, ...rest } = projectResponse;
	return {
		...rest,
		selectedProject: project ? transformProjectEntity(project) : null,
	};
};

const transformIssueTypeEntity = ({ id, name, avatar, hierarchy }: IssueTypeEntity): IssueType => ({
	label: name,
	value: id,
	iconUrl: avatar.large,
	type: ISSUE_TYPE,
	hierarchy: hierarchy?.level,
});

const transformIssueTypes = (issueTypes: IssueTypeEntity[]): IssueType[] =>
	issueTypes.map((issueType) => transformIssueTypeEntity(issueType));

const transformIssueTypeField = (issueTypeResponse: JiraIssueTypeField): TransformedIssueField => {
	const { issueType, ...rest } = issueTypeResponse;
	const issueTypes = get(issueTypeResponse, 'issueTypes.nodes', []);
	return {
		...rest,
		selectedIssueType: issueType ? transformIssueTypeEntity(issueType) : null,
		issueTypes: transformIssueTypes(issueTypes),
	};
};

const transformRequestTypeEntity = (
	{
		requestTypeId,
		name,
		description,
		helpText,
		key,
		avatar,
		issueType: { issueTypeId, issueTypeName },
		practices,
	}: JsmRequestType & { issueType: { issueTypeId: string; issueTypeName?: string } },
	hasPractice: boolean,
): TransformedRequestType => ({
	label: name,
	description: description || '',
	helpText: helpText || '',
	value: `${issueTypeId}-${requestTypeId}`,
	iconUrl: transformStargateUrl(avatar.large || ''),
	requestTypeKey: key,
	issueTypeId,
	issueTypeName,
	practice: get(practices, '0.key', hasPractice ? UNASSIGNED_REQUEST_TYPE : REQUEST_TYPE),
	type: REQUEST_TYPE,
});

const transformRequestTypes = (requestTypes: JsmRequestType[]): TransformedRequestType[] => {
	const hasPractice = requestTypes.some((rt) => rt.practices.length > 0);
	// sort request types alphabetically
	return requestTypes
		.map((requestType) => transformRequestTypeEntity(requestType, hasPractice))
		.sort((a, b) => a.label.localeCompare(b.label));
};

const transformRequestTypeField = (
	requestTypeResponse: JsmRequestTypeField,
): TransformedRequestField => {
	const { requestType, ...rest } = requestTypeResponse;
	const requestTypes = requestTypeResponse.requestTypes.nodes;
	const hasPractice = requestTypes.some((rt) => rt.practices.length > 0);

	return {
		...rest,
		selectedRequestType: requestType ? transformRequestTypeEntity(requestType, hasPractice) : null,
		requestTypes: transformRequestTypes(requestTypes),
	};
};

export {
	transformProjectEntity,
	transformProjects,
	transformProjectField,
	transformIssueTypeEntity,
	transformIssueTypes,
	transformIssueTypeField,
	transformRequestTypeEntity,
	transformRequestTypes,
	transformRequestTypeField,
};
